
import crmService from "@/modules/CRM/services/crmService";

const OPORTUNIDADES_PER_PAGE = 20;

const CHART_COLORS = [
  "#f64e60",
  "#ffa800",
  "#1bc5bd",
  "#3699ff",
  "#8950fc",
  "#3f4254",
  "#50cd89",
  "#e4a11b",
];

const ReportCard = {
  functional: true,
  props: {
    title: {
      type: String,
      required: true,
    },
  },
  render(createElement, context) {
    return createElement("div", { class: "card card-custom gutter-b" }, [
      createElement("div", { class: "card-header" }, [
        createElement("div", { class: "card-title" }, [
          createElement("h3", { class: "card-label" }, context.props.title),
        ]),
      ]),
      createElement("div", { class: "card-body" }, context.children),
    ]);
  },
};

const CrmContatoTable = {
  functional: true,
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  render(createElement, context) {
    const rows = context.props.items.map((item) =>
      createElement("tr", { key: item.id_crm_contato }, [
        createElement("td", item.nome || item.pessoa_nome || "-"),
        createElement("td", item.telefone || "-"),
        createElement("td", item.email || "-"),
        createElement("td", item.usuario || "-"),
        createElement("td", item.status || "-"),
        createElement("td", formatDateTimeValue(item.created_at)),
      ])
    );

    if (!rows.length) {
      rows.push(
        createElement("tr", { key: "empty" }, [
          createElement(
            "td",
            { attrs: { colspan: "6" } },
            "Nenhum registro encontrado."
          ),
        ])
      );
    }

    return createElement("div", { class: "crm-report-table" }, [
      createElement("table", [
        createElement("thead", [
          createElement("tr", [
            createElement("th", "Nome"),
            createElement("th", "Telefone"),
            createElement("th", "E-mail"),
            createElement("th", "Responsável"),
            createElement("th", "Status"),
            createElement("th", "Criado em"),
          ]),
        ]),
        createElement("tbody", rows),
      ]),
    ]);
  },
};

const OportunidadesTable = {
  functional: true,
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    formatMoney: {
      type: Function,
      required: true,
    },
    formatDateTime: {
      type: Function,
      required: true,
    },
    statusLabel: {
      type: Function,
      required: true,
    },
    valorOportunidade: {
      type: Function,
      required: true,
    },
  },
  render(createElement, context) {
    const props = context.props;
    const rows = props.items.map((item) =>
      createElement("tr", { key: item.id_crm_oportunidade }, [
        createElement("td", [
          createElement(
            "router-link",
            {
              props: {
                to: {
                  name: "crmRelatorioOportunidadeDetalhe",
                  params: { id: item.id_crm_oportunidade },
                  query: { origem: "relatorios" },
                },
              },
            },
            item.titulo || "-"
          ),
        ]),
        createElement("td", item.contato || item.pessoa || "-"),
        createElement("td", item.funil || "-"),
        createElement("td", item.etapa || "-"),
        createElement("td", props.statusLabel(item.status)),
        createElement("td", props.formatMoney(props.valorOportunidade(item))),
        createElement("td", props.formatDateTime(item.proxima_atividade_em)),
      ])
    );

    if (!rows.length) {
      rows.push(
        createElement("tr", { key: "empty" }, [
          createElement(
            "td",
            { attrs: { colspan: "7" } },
            "Nenhuma oportunidade encontrada."
          ),
        ])
      );
    }

    return createElement("div", { class: "crm-report-table" }, [
      createElement("table", [
        createElement("thead", [
          createElement("tr", [
            createElement("th", "Título"),
            createElement("th", "Contato"),
            createElement("th", "Funil"),
            createElement("th", "Etapa"),
            createElement("th", "Status"),
            createElement("th", "Valor"),
            createElement("th", "Próxima ação"),
          ]),
        ]),
        createElement("tbody", rows),
      ]),
    ]);
  },
};

function formatDateTimeValue(value) {
  if (!value) return "-";
  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR");
}

export default {
  name: "crm-relatorios-operacionais",
  components: {
    CrmContatoTable,
    OportunidadesTable,
    ReportCard,
  },
  data() {
    return {
      activeReport: "resumo",
      agenda: [],
      agendaResponsavel: [],
      agendaStatus: [],
      clientes: [],
      contatos: [],
      dashboardResumo: {},
      drillDownLabel: "",
      filtros: this.defaultPeriodo(),
      fontes: [],
      funil: [],
      funis: [],
      loading: false,
      origens: [],
      oportunidades: [],
      paginacao: {
        agenda: {},
        clientes: {},
        contatos: {},
        oportunidades: {},
        solicitacoes: {},
        tarefas: {},
      },
      paginas: {
        agenda: 1,
        clientes: 1,
        contatos: 1,
        oportunidades: 1,
        solicitacoes: 1,
        tarefas: 1,
      },
      perdas: [],
      produtividade: [],
      solicitacoes: [],
      tarefas: [],
      temperaturas: [],
      tiposTarefa: [],
      usuarios: [],
    };
  },
  computed: {
    agendaTecnicaChartOptions() {
      return this.barChartOptions(
        this.agendaTecnicaStatus.map(
          (item) => item.status_label || item.status
        ),
        (index) => this.drillAgendaTecnica(this.agendaTecnicaStatus[index]),
        false,
        CHART_COLORS.slice(2)
      );
    },
    agendaTecnicaChartSeries() {
      return [
        {
          name: "Solicitações",
          data: this.agendaTecnicaStatus.map((item) => Number(item.total || 0)),
        },
      ];
    },
    agendaTecnicaStatus() {
      return this.normalizeCollection(
        this.dashboardResumo.agenda_tecnica_por_status || []
      );
    },
    etapaOptions() {
      const funilSelecionado = this.funis.find(
        (funil) => funil.id_crm_funil === this.filtros.id_crm_funil
      );
      const etapas = funilSelecionado ? funilSelecionado.etapas || [] : [];
      return [{ value: null, text: "Todas" }].concat(
        etapas.map((etapa) => ({
          value: etapa.id_crm_etapa,
          text: etapa.nome,
        }))
      );
    },
    evolucaoChartOptions() {
      const valores = this.evolucaoOportunidades.reduce((acumulador, item) => {
        acumulador.push(
          Number(item.abertas || 0),
          Number(item.ganhas || 0),
          Number(item.perdidas || 0)
        );
        return acumulador;
      }, []);
      const maxY = Math.max(0, ...valores);
      const tickAmount = maxY <= 5 ? Math.max(1, maxY) : 5;

      return {
        chart: {
          events: {
            dataPointSelection: (event, chartContext, config) => {
              this.drillEvolucao(
                this.evolucaoOportunidades[config.dataPointIndex]
              );
            },
          },
          fontFamily: "Poppins, Helvetica, sans-serif",
          toolbar: { show: false },
        },
        colors: ["#3699ff", "#1bc5bd", "#f64e60"],
        dataLabels: { enabled: false },
        fill: {
          opacity: 0.2,
          type: "solid",
        },
        grid: { borderColor: "#edf2f7" },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        tooltip: {
          y: {
            formatter: (value) => `${Math.round(value)} oportunidade(s)`,
          },
        },
        xaxis: {
          categories: this.evolucaoOportunidades.map((item) =>
            this.formatDate(item.periodo)
          ),
        },
        yaxis: {
          min: 0,
          max: maxY <= 5 ? Math.max(1, maxY) : undefined,
          tickAmount,
          labels: {
            formatter: (value) => Math.round(Number(value) || 0),
          },
        },
      };
    },
    evolucaoChartSeries() {
      return [
        {
          name: "Abertas",
          data: this.evolucaoOportunidades.map((item) =>
            Number(item.abertas || 0)
          ),
        },
        {
          name: "Ganhas",
          data: this.evolucaoOportunidades.map((item) =>
            Number(item.ganhas || 0)
          ),
        },
        {
          name: "Perdidas",
          data: this.evolucaoOportunidades.map((item) =>
            Number(item.perdidas || 0)
          ),
        },
      ];
    },
    evolucaoOportunidades() {
      return this.normalizeCollection(
        this.dashboardResumo.evolucao_oportunidades || []
      );
    },
    fonteOptions() {
      return [{ value: null, text: "Todas" }].concat(
        this.fontes.map((fonte) => ({
          value: fonte.id_crm_fonte,
          text: fonte.nome,
        }))
      );
    },
    funilOptions() {
      return [{ value: null, text: "Todos" }].concat(
        this.funis.map((funil) => ({
          value: funil.id_crm_funil,
          text: funil.nome,
        }))
      );
    },
    funnelChartOptions() {
      return {
        chart: {
          events: {
            dataPointSelection: (event, chartContext, config) => {
              this.drillOportunidadePorEtapa(
                this.funnelVisualItems[config.dataPointIndex]
              );
            },
          },
          fontFamily: "Poppins, Helvetica, sans-serif",
          parentHeightOffset: 0,
          toolbar: { show: false },
        },
        colors: this.funnelVisualItems.map((item) => item.color),
        dataLabels: {
          enabled: true,
          formatter: (value, options) => {
            const item = this.funnelVisualItems[options.dataPointIndex];
            return item ? `${item.etapa} · ${item.total}` : value;
          },
          style: {
            colors: ["#ffffff"],
            fontSize: "12px",
            fontWeight: 800,
          },
        },
        grid: { show: false },
        legend: { show: false },
        plotOptions: {
          bar: {
            barHeight: "82%",
            borderRadius: 4,
            distributed: true,
            horizontal: true,
            isFunnel: true,
          },
        },
        stroke: {
          colors: ["#ffffff"],
          width: 2,
        },
        tooltip: {
          y: {
            formatter: (value, options) => {
              const item = this.funnelVisualItems[options.dataPointIndex];
              if (!item) return `${Math.round(value)} oportunidade(s)`;
              return `${Math.round(value)} oportunidade(s) · ${this.formatMoney(
                item.valor
              )}`;
            },
          },
        },
        xaxis: {
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { show: false },
        },
        yaxis: { labels: { show: false } },
      };
    },
    funnelChartSeries() {
      return [
        {
          name: "Oportunidades",
          data: this.funnelVisualItems.map((item) => ({
            x: item.etapa,
            y: item.total,
            fillColor: item.color,
          })),
        },
      ];
    },
    funilGrafico() {
      const dashboard = this.normalizeCollection(
        this.dashboardResumo.funil_por_etapa || []
      );
      return dashboard.length ? dashboard : this.funil;
    },
    funnelMaiorEtapa() {
      const [maiorEtapa] = [...this.funnelVisualItems].sort(
        (a, b) => Number(b.total || 0) - Number(a.total || 0)
      );
      return maiorEtapa?.etapa || "-";
    },
    funnelTotalOportunidades() {
      return this.funnelVisualItems.reduce(
        (total, item) => total + Number(item.total || 0),
        0
      );
    },
    funnelValorTotal() {
      return this.funnelVisualItems.reduce(
        (total, item) => total + Number(item.valor || 0),
        0
      );
    },
    funnelVisualItems() {
      const maxTotal = Math.max(
        1,
        ...this.funilGrafico.map((item) => Number(item.total || 0))
      );

      return this.funilGrafico
        .filter((item) => Number(item.total || 0) > 0)
        .map((item, index) => {
          const total = Number(item.total || 0);
          const width = Math.max(28, Math.round((total / maxTotal) * 100));
          return {
            id: item.id_crm_etapa,
            etapa: item.etapa || "Sem etapa",
            total,
            valor: Number(item.valor || 0),
            width: `${width}%`,
            color: CHART_COLORS[index % CHART_COLORS.length],
          };
        });
    },
    origemAgendaOptions() {
      return [
        { value: "todos", text: "Agenda e solicitações" },
        { value: "agenda", text: "Somente agenda" },
        { value: "solicitacoes", text: "Somente solicitações" },
      ];
    },
    perdasChartOptions() {
      return this.barChartOptions(
        this.perdasResumo.map((item) => item.motivo || "Sem motivo"),
        (index) => this.drillOportunidadePorPerda(this.perdasResumo[index]),
        true,
        ["#f64e60", "#ffa800", "#8950fc", "#3699ff"]
      );
    },
    perdasChartSeries() {
      return [
        {
          name: "Perdas",
          data: this.perdasResumo.map((item) => Number(item.total || 0)),
        },
      ];
    },
    perdasResumo() {
      const dashboard = this.normalizeCollection(
        this.dashboardResumo.perdas || []
      );
      return dashboard.length ? dashboard : this.perdas;
    },
    periodoLabel() {
      return `${this.formatDate(
        this.filtros.data_inicio
      )} até ${this.formatDate(this.filtros.data_fim)}`;
    },
    periodoTarefaOptions() {
      return [
        { value: "", text: "Período informado" },
        { value: "hoje", text: "Hoje" },
        { value: "atrasadas", text: "Atrasadas" },
        { value: "proximas", text: "Próximas" },
      ];
    },
    rankingChartOptions() {
      return this.barChartOptions(
        this.rankingResponsaveis.map(
          (item) => item.responsavel || "Sem responsável"
        ),
        (index) =>
          this.drillOportunidadePorResponsavel(this.rankingResponsaveis[index]),
        true,
        ["#3699ff", "#1bc5bd", "#8950fc", "#ffa800"],
        (value) => this.formatMoney(value)
      );
    },
    rankingChartSeries() {
      return [
        {
          name: "Valor",
          data: this.rankingResponsaveis.map((item) => Number(item.valor || 0)),
        },
      ];
    },
    rankingResponsaveis() {
      return this.normalizeCollection(
        this.dashboardResumo.ranking_responsaveis || []
      );
    },
    pipelineValoresChartOptions() {
      return this.barChartOptions(
        this.pipelineValoresItems.map((item) => item.label || item.chave),
        () => {},
        false,
        this.pipelineValoresItems.map(
          (item) => item.cor || item.color || "#3699ff"
        ),
        (value) => this.formatMoney(value)
      );
    },
    pipelineValoresChartSeries() {
      return [
        {
          name: "Valor",
          data: this.pipelineValoresItems.map((item) =>
            Number(item.valor || 0)
          ),
        },
      ];
    },
    pipelineValoresItems() {
      return this.normalizeCollection(
        this.dashboardResumo.valores_pipeline_comercial || []
      ).filter(
        (item) => Number(item.valor || 0) > 0 || Number(item.total || 0) > 0
      );
    },
    reportTabs() {
      return [
        { key: "resumo", label: "Resumo" },
        { key: "oportunidades", label: "Oportunidades" },
        { key: "clientes", label: "Clientes" },
        { key: "contatos", label: "Contatos" },
        { key: "tarefas", label: "Tarefas" },
        { key: "agenda", label: "Agenda/Solicitações" },
      ];
    },
    statusChartOptions() {
      return {
        chart: {
          events: {
            dataPointSelection: (event, chartContext, config) => {
              this.drillOportunidadePorStatus(
                this.statusConversao[config.dataPointIndex]
              );
            },
          },
          fontFamily: "Poppins, Helvetica, sans-serif",
          toolbar: { show: false },
        },
        colors: ["#3699ff", "#1bc5bd", "#f64e60"],
        dataLabels: { enabled: false },
        labels: this.statusConversao.map((item) =>
          this.statusLabel(item.status)
        ),
        legend: { position: "bottom" },
        tooltip: {
          y: {
            formatter: (value) => `${Math.round(value)} oportunidade(s)`,
          },
        },
      };
    },
    statusChartSeries() {
      return this.statusConversao.map((item) => Number(item.total || 0));
    },
    statusConversao() {
      return this.normalizeCollection(
        this.dashboardResumo.status_conversao || []
      );
    },
    statusOptions() {
      if (this.activeReport === "tarefas") {
        return [
          { value: "Todos", text: "Todos" },
          { value: "aberta", text: "Abertas" },
          { value: "concluida", text: "Concluídas" },
        ];
      }

      if (this.activeReport === "agenda") {
        return [
          { value: "Todos", text: "Todos" },
          { value: "agendado", text: "Agendados" },
          { value: "confirmado", text: "Confirmados" },
          { value: "concluido", text: "Concluídos" },
          { value: "cancelado", text: "Cancelados" },
          { value: "nao_compareceu", text: "Não compareceu" },
          { value: "solicitada", text: "Solicitada" },
          { value: "link_enviado", text: "Link enviado" },
          { value: "agendada", text: "Solicitação agendada" },
          { value: "concluida", text: "Solicitação concluída" },
          { value: "cancelada", text: "Solicitação cancelada" },
        ];
      }

      if (["clientes", "contatos"].includes(this.activeReport)) {
        return [
          { value: "Todos", text: "Todos" },
          { value: "ativo", text: "Ativos" },
          { value: "inativo", text: "Inativos" },
        ];
      }

      return [
        { value: "Todos", text: "Todos" },
        { value: "aberta", text: "Abertas" },
        { value: "ganha", text: "Ganhas" },
        { value: "perdida", text: "Perdidas" },
      ];
    },
    tarefasChartOptions() {
      return this.barChartOptions(
        this.tarefasPorVencimento.map((item) => item.label || item.chave),
        (index) => this.drillTarefas(this.tarefasPorVencimento[index]),
        false,
        ["#f64e60", "#ffa800", "#1bc5bd", "#3699ff", "#7e8299"]
      );
    },
    tarefasChartSeries() {
      return [
        {
          name: "Tarefas",
          data: this.tarefasPorVencimento.map((item) =>
            Number(item.total || 0)
          ),
        },
      ];
    },
    tarefasPorVencimento() {
      return this.normalizeCollection(
        this.dashboardResumo.tarefas_por_vencimento || []
      );
    },
    temperaturaChartOptions() {
      return {
        chart: {
          events: {
            dataPointSelection: (event, chartContext, config) => {
              this.drillOportunidadePorTemperatura(
                this.temperaturasResumo[config.dataPointIndex]
              );
            },
          },
          fontFamily: "Poppins, Helvetica, sans-serif",
          toolbar: { show: false },
        },
        colors: this.temperaturasResumo.map(
          (item, index) => item.cor || CHART_COLORS[index % CHART_COLORS.length]
        ),
        dataLabels: { enabled: false },
        labels: this.temperaturasResumo.map(
          (item) => item.label || "Sem temperatura"
        ),
        legend: { position: "bottom" },
        tooltip: {
          y: {
            formatter: (value) => `${Math.round(value)} oportunidade(s)`,
          },
        },
      };
    },
    temperaturaChartSeries() {
      return this.temperaturasResumo.map((item) => Number(item.total || 0));
    },
    temperaturaOptions() {
      return [
        { value: "Todos", text: "Todas" },
        { value: "__sem_temperatura__", text: "Sem temperatura" },
      ].concat(
        this.temperaturas.map((temperatura) => ({
          value: temperatura.chave || temperatura.nome,
          text: temperatura.nome,
        }))
      );
    },
    temperaturasResumo() {
      return this.normalizeCollection(this.dashboardResumo.temperaturas || []);
    },
    tipoOptions() {
      return [{ value: "Todos", text: "Todos" }].concat(
        this.tiposTarefa.map((tipo) => ({
          value: tipo.chave || tipo.nome,
          text: tipo.nome,
        }))
      );
    },
    usuarioOptions() {
      return [{ value: null, text: "Todos" }].concat(
        this.usuarios.map((usuario) => ({
          value: usuario.id_usuario,
          text: usuario.nome || usuario.usuario,
        }))
      );
    },
  },
  watch: {
    "filtros.id_crm_funil"() {
      this.filtros.id_crm_etapa = null;
    },
  },
  async mounted() {
    await this.loadBase();
    this.loadRelatorios();
  },
  methods: {
    barChartOptions(
      categories,
      onSelect,
      horizontal = false,
      colors = CHART_COLORS,
      valueFormatter = null
    ) {
      return {
        chart: {
          events: {
            dataPointSelection: (event, chartContext, config) => {
              onSelect(config.dataPointIndex);
            },
          },
          fontFamily: "Poppins, Helvetica, sans-serif",
          toolbar: { show: false },
        },
        colors,
        dataLabels: { enabled: false },
        grid: { borderColor: "#edf2f7" },
        legend: { show: false },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "48%",
            distributed: true,
            horizontal,
          },
        },
        tooltip: {
          y: {
            formatter: (value) => {
              if (valueFormatter) return valueFormatter(value);
              return `${Math.round(value)} registro(s)`;
            },
          },
        },
        xaxis: {
          categories,
          labels: {
            trim: true,
            rotate: horizontal ? 0 : -20,
            formatter: (value) => {
              if (horizontal && valueFormatter) return valueFormatter(value);
              return value;
            },
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              if (horizontal) return value;
              if (valueFormatter) return valueFormatter(value);
              return Math.round(value);
            },
          },
        },
      };
    },
    aplicarFiltros() {
      this.resetPage();
      this.loadRelatorios();
    },
    async changePage(key, page) {
      this.$set(this.paginas, key, page);

      if (key === "oportunidades") {
        this.loading = true;
        try {
          await this.loadOportunidades();
        } catch (error) {
          this.$toast.error("Não foi possível carregar a página selecionada.");
        } finally {
          this.loading = false;
        }
        return;
      }

      this.loadRelatorios();
    },
    resetPage(key = null) {
      if (key) {
        this.$set(this.paginas, key, 1);
        return;
      }

      Object.keys(this.paginas).forEach((paginaKey) => {
        this.$set(this.paginas, paginaKey, 1);
      });
    },
    shouldShowPagination(key) {
      const meta = this.paginacao[key] || {};
      const total = Number(meta.total || 0);
      const perPage = Number(meta.per_page || OPORTUNIDADES_PER_PAGE);

      return total > perPage;
    },
    async loadBase() {
      try {
        const [funis, usuarios, fontes, tipos, temperaturas] =
          await Promise.all([
            crmService.funis(),
            crmService.usuarios({
              per_page: 100,
              page: 1,
              sort: [{ field: "nome", type: "asc" }],
            }),
            crmService.fontesConfiguracao(),
            crmService.tiposTarefaConfiguracao(),
            crmService.temperaturasConfiguracao(),
          ]);
        this.funis = this.normalizeCollection(this.responseData(funis));
        this.usuarios = this.normalizeCollection(this.responseData(usuarios));
        this.fontes = this.normalizeCollection(this.responseData(fontes));
        this.tiposTarefa = this.normalizeCollection(this.responseData(tipos));
        this.temperaturas = this.normalizeCollection(
          this.responseData(temperaturas)
        );
      } catch (error) {
        this.$toast.error("Não foi possível carregar filtros do CRM.");
      }
    },
    async loadRelatorios() {
      if (!this.filtros.data_inicio || !this.filtros.data_fim) {
        this.$toast.error("Informe o período para carregar os relatórios.");
        return;
      }

      this.loading = true;
      try {
        if (this.activeReport === "resumo") {
          await this.loadResumo();
        } else if (this.activeReport === "oportunidades") {
          await this.loadOportunidades();
        } else if (this.activeReport === "clientes") {
          await this.loadClientes();
        } else if (this.activeReport === "contatos") {
          await this.loadContatos();
        } else if (this.activeReport === "tarefas") {
          await this.loadTarefas();
        } else if (this.activeReport === "agenda") {
          await this.loadAgendaOperacional();
        }
      } catch (error) {
        this.$toast.error("Não foi possível carregar os relatórios CRM.");
      } finally {
        this.loading = false;
      }
    },
    async loadAgendaOperacional() {
      const payload = this.operationalPayload();

      if (this.filtros.origem_agenda !== "solicitacoes") {
        const agenda = await crmService.relatorioAgendaLista(payload);
        const agendaPage = this.normalizePage(this.responseData(agenda));
        this.agenda = agendaPage.items;
        this.paginacao.agenda = agendaPage.meta;
      } else {
        this.agenda = [];
        this.paginacao.agenda = {};
      }

      if (this.filtros.origem_agenda !== "agenda") {
        const solicitacoes = await crmService.relatorioSolicitacoesAgenda(
          payload
        );
        const solicitacoesPage = this.normalizePage(
          this.responseData(solicitacoes)
        );
        this.solicitacoes = solicitacoesPage.items;
        this.paginacao.solicitacoes = solicitacoesPage.meta;
      } else {
        this.solicitacoes = [];
        this.paginacao.solicitacoes = {};
      }
    },
    async loadClientes() {
      const response = await crmService.relatorioClientesLista(
        this.operationalPayload()
      );
      const page = this.normalizePage(this.responseData(response));
      this.clientes = page.items;
      this.paginacao.clientes = page.meta;
    },
    async loadContatos() {
      const response = await crmService.relatorioContatosLista(
        this.operationalPayload()
      );
      const page = this.normalizePage(this.responseData(response));
      this.contatos = page.items;
      this.paginacao.contatos = page.meta;
    },
    async loadOportunidades(payload = this.oportunidadesPayload()) {
      const response = await crmService.relatorioOportunidadesLista(payload);
      const page = this.normalizePage(this.responseData(response));
      this.oportunidades = page.items;
      this.paginacao.oportunidades = page.meta;
      this.$set(this.paginas, "oportunidades", page.meta.current_page);
    },
    async loadResumo() {
      const payload = this.summaryPayload();
      const [dashboard, funil, perdas, agenda, produtividade, origens] =
        await Promise.all([
          crmService.dashboard(payload),
          crmService.relatorioFunil(payload),
          crmService.relatorioPerdas(payload),
          crmService.relatorioAgenda(payload),
          crmService.relatorioProdutividade(payload),
          crmService.relatorioOrigens(payload),
        ]);

      const funilData = this.responseData(funil);
      const perdasData = this.responseData(perdas);
      const agendaData = this.responseData(agenda);
      const produtividadeData = this.responseData(produtividade);
      const origensData = this.responseData(origens);

      this.dashboardResumo = this.responseData(dashboard);
      this.funil = this.normalizeCollection(funilData.itens || funilData);
      this.perdas = this.normalizeCollection(perdasData.itens || perdasData);
      this.agendaStatus = this.normalizeCollection(agendaData.por_status || []);
      this.agendaResponsavel = this.normalizeCollection(
        agendaData.por_responsavel || []
      );
      this.produtividade = this.normalizeCollection(
        produtividadeData.itens || produtividadeData
      );
      this.origens = this.normalizeCollection(origensData.itens || origensData);
      await this.loadOportunidades(this.oportunidadesPayload());
    },
    async loadTarefas() {
      const response = await crmService.relatorioTarefasLista(
        this.operationalPayload()
      );
      const page = this.normalizePage(this.responseData(response));
      this.tarefas = page.items;
      this.paginacao.tarefas = page.meta;
    },
    limparDrillDown() {
      this.drillDownLabel = "";
      this.filtros.id_crm_etapa = null;
      this.filtros.id_crm_motivo_perda = null;
      this.filtros.id_usuario = null;
      this.filtros.periodo = "";
      this.filtros.status = "Todos";
      this.filtros.temperatura = "Todos";
      this.resetPage("oportunidades");
      this.loadRelatorios();
    },
    limparFiltros() {
      this.drillDownLabel = "";
      this.filtros = this.defaultPeriodo();
      this.resetPage();
      this.loadRelatorios();
    },
    drillAgendaTecnica(item) {
      if (!item) return;
      this.activeReport = "agenda";
      this.filtros.status = item.status || "Todos";
      this.filtros.origem_agenda = "solicitacoes";
      this.resetPage("agenda");
      this.resetPage("solicitacoes");
      this.loadRelatorios();
    },
    drillEvolucao(item) {
      if (!item || !item.periodo) return;
      this.filtros.data_inicio = item.periodo;
      this.filtros.data_fim = item.periodo;
      this.drillDownLabel = `Período: ${this.formatDate(item.periodo)}`;
      this.resetPage("oportunidades");
      this.loadRelatorios();
    },
    drillOportunidadePorEtapa(item) {
      if (!item) return;
      this.filtros.id_crm_etapa = item.id || null;
      this.drillDownLabel = `Etapa: ${item.etapa}`;
      this.resetPage("oportunidades");
      this.loadRelatorios();
    },
    drillOportunidadePorPerda(item) {
      if (!item) return;
      this.filtros.status = "perdida";
      this.filtros.id_crm_motivo_perda = item.id_crm_motivo_perda || null;
      this.drillDownLabel = `Motivo de perda: ${item.motivo || "Sem motivo"}`;
      this.resetPage("oportunidades");
      this.loadRelatorios();
    },
    drillOportunidadePorResponsavel(item) {
      if (!item || !item.id_usuario) return;
      this.filtros.id_usuario = item.id_usuario;
      this.drillDownLabel = `Responsável: ${item.responsavel}`;
      this.resetPage("oportunidades");
      this.loadRelatorios();
    },
    drillOportunidadePorStatus(item) {
      if (!item) return;
      this.filtros.status = item.status || "Todos";
      this.drillDownLabel = `Status: ${this.statusLabel(item.status)}`;
      this.resetPage("oportunidades");
      this.loadRelatorios();
    },
    drillOportunidadePorTemperatura(item) {
      if (!item) return;
      this.filtros.temperatura = item.temperatura || "__sem_temperatura__";
      this.drillDownLabel = `Temperatura: ${item.label || "Sem temperatura"}`;
      this.resetPage("oportunidades");
      this.loadRelatorios();
    },
    drillTarefas(item) {
      if (!item) return;
      const map = {
        atrasadas: "atrasadas",
        hoje: "hoje",
        proximas_sete_dias: "proximas",
        acima_sete_dias: "proximas",
        sem_vencimento: "",
      };
      this.activeReport = "tarefas";
      this.filtros.status =
        item.chave === "sem_vencimento" ? "Todos" : "aberta";
      this.filtros.periodo = map[item.chave] || "";
      this.resetPage("tarefas");
      this.loadRelatorios();
    },
    operationalPayload(report = this.activeReport) {
      const payload = {
        data_inicio: this.filtros.data_inicio,
        data_fim: this.filtros.data_fim,
        id_usuario: this.filtros.id_usuario,
        status: this.filtros.status,
        busca: this.filtros.busca,
        page: this.paginas[report] || 1,
        per_page: report === "oportunidades" ? OPORTUNIDADES_PER_PAGE : 50,
      };

      if (report === "oportunidades") {
        payload.id_crm_funil = this.filtros.id_crm_funil;
        payload.id_crm_etapa = this.filtros.id_crm_etapa;
        payload.id_crm_fonte = this.filtros.id_crm_fonte;
        payload.id_crm_motivo_perda = this.filtros.id_crm_motivo_perda;
        payload.temperatura = this.filtros.temperatura;
        payload.busca_notas = this.filtros.busca_notas;
        payload.tipo_relatorio = "comercial";
        payload.somente_oportunidades_raiz = true;
      }

      if (["tarefas", "agenda"].includes(report)) {
        payload.tipo = this.filtros.tipo;
      }

      if (report === "tarefas") {
        payload.periodo = this.filtros.periodo;
      }

      return payload;
    },
    oportunidadesPayload() {
      return this.operationalPayload("oportunidades");
    },
    selectReport(report) {
      this.activeReport = report;
      this.drillDownLabel = "";
      this.filtros.id_crm_motivo_perda = null;
      this.filtros.status = "Todos";
      this.filtros.tipo = "Todos";
      this.resetPage(report === "resumo" ? "oportunidades" : report);
      this.loadRelatorios();
    },
    summaryPayload() {
      return {
        data_inicio: this.filtros.data_inicio,
        data_fim: this.filtros.data_fim,
        id_crm_funil: this.filtros.id_crm_funil,
        id_crm_etapa: this.filtros.id_crm_etapa,
        id_usuario: this.filtros.id_usuario,
        id_crm_fonte: this.filtros.id_crm_fonte,
        id_crm_motivo_perda: this.filtros.id_crm_motivo_perda,
        status: this.filtros.status,
        temperatura: this.filtros.temperatura,
        tipo_relatorio: "comercial",
        somente_oportunidades_raiz: true,
      };
    },
    defaultPeriodo() {
      const hoje = new Date();
      const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      return {
        busca: "",
        busca_notas: "",
        data_inicio: this.toDateInput(inicio),
        data_fim: this.toDateInput(hoje),
        id_crm_etapa: null,
        id_crm_fonte: null,
        id_crm_funil: null,
        id_crm_motivo_perda: null,
        id_usuario: null,
        origem_agenda: "todos",
        periodo: "",
        status: "Todos",
        temperatura: "Todos",
        tipo: "Todos",
      };
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(`${value}T00:00:00`).toLocaleDateString("pt-BR");
    },
    formatDateTime(value) {
      return formatDateTimeValue(value);
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    normalizeCollection(data) {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.data)) return data.data;
      return [];
    },
    normalizePage(data) {
      const items = this.normalizeCollection(data);
      const total = data && data.total ? Number(data.total) : items.length;
      const perPage =
        data && data.per_page ? Number(data.per_page) : OPORTUNIDADES_PER_PAGE;

      return {
        items,
        meta: {
          current_page:
            data && data.current_page ? Number(data.current_page) : 1,
          last_page:
            data && data.last_page
              ? Number(data.last_page)
              : Math.max(1, Math.ceil(total / perPage)),
          per_page: perPage,
          total,
        },
      };
    },
    responseData(response) {
      const data = response ? response.data : null;
      return data && data.data ? data.data : data || {};
    },
    statusAgendaLabel(status) {
      const labels = {
        agendado: "Agendado",
        confirmado: "Confirmado",
        concluido: "Concluído",
        cancelado: "Cancelado",
        nao_compareceu: "Não compareceu",
      };
      return labels[status] || status || "-";
    },
    statusAtividadeLabel(status) {
      const labels = {
        aberta: "Aberta",
        concluida: "Concluída",
      };
      return labels[status] || status || "-";
    },
    statusLabel(status) {
      const labels = {
        aberta: "Aberta",
        ganha: "Ganha",
        perdida: "Perdida",
      };
      return labels[status] || status || "-";
    },
    statusSolicitacaoLabel(status) {
      const labels = {
        solicitada: "Solicitada",
        link_enviado: "Link enviado",
        agendada: "Agendada",
        concluida: "Concluída",
        cancelada: "Cancelada",
      };
      return labels[status] || status || "-";
    },
    tipoLabel(tipo) {
      const encontrado = this.tiposTarefa.find(
        (item) => item.chave === tipo || item.nome === tipo
      );
      return encontrado ? encontrado.nome : tipo || "-";
    },
    toDateInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    valorOportunidade(item) {
      return (
        item.valor_final ||
        item.valor_negociacao ||
        item.valor_proposta ||
        item.valor_estimado ||
        0
      );
    },
  },
};
